import { useId } from 'react';
import Svg, {
  Circle,
  ClipPath,
  Defs,
  G,
  LinearGradient,
  Path,
  Rect,
  Stop,
} from 'react-native-svg';
import { useThemeColors } from '@kiips/ui';

/**
 * The kiips brand logo as a themed vector — a "K" mark (a full-height gutter
 * splitting a rounded square into two boxes, with a left-pointing wedge and a
 * hole on the right edge) plus the monoline "kiips" wordmark.
 *
 * Geometry mirrors `scripts/lib/brand.mjs`, the generator for the static SVG /
 * PNG brand assets. Keep the two in sync; this component exists because the
 * top bar needs theme-reactive colours (the wordmark = textOnSurface, the
 * knockout reads as the surface it sits on) that a baked SVG file can't
 * provide. By default the mark panels are painted in the "Aurora" cyan→violet
 * gradient (mirrors AURORA in brand.mjs); pass `mono` for the monochrome mark.
 */

// Aurora brand gradient (cyan → violet), painted diagonally across the panels.
// Mirrors AURORA in scripts/lib/brand.mjs — keep the two stop lists equal.
const AURORA = ['#06B6D4', '#7C3AED'] as const;

const CORNER = 0.225; // squircle corner radius (fraction of side)
// "K" geometry (fractions of the side): a full-height white gutter splits the
// square into two black boxes — a flat left box (square gutter edge) and a
// right box whose gutter-side corners are rounded to mirror the squircle. A
// large left-pointing wedge knocks into the right box; its head + hole sit on
// the right edge (clipped to the box, turning the overrun into the edge bite).
const GUTTER = { cx: 0.285, w: 0.1 };
const RBOX_R = 0.225;
const WEDGE = { tipX: 0.24, midY: 0.5, headCx: 0.95, headR: 0.32 };
const HOLE = { cx: 0.95, r: 0.13 };

// Wordmark metrics (em-space): baseline 300, x-height top 100, k ascends to
// 12, p descends to 372.
const WM = { B: 300, xhTop: 100, kTop: 12, pBot: 372, W: 34 };

/** Wedge outline: a left-pointing tip fanning out to a round head at the right. */
function wedgePath(S: number): string {
  const Tx = WEDGE.tipX * S, Ty = WEDGE.midY * S;
  const Cx = WEDGE.headCx * S, Cy = WEDGE.midY * S, r = WEDGE.headR * S;
  const d = Cx - Tx;
  const a = Math.acos(r / d);
  const base = Math.PI;
  const Rx = Cx + r * Math.cos(base - a), Ry = Cy + r * Math.sin(base - a);
  const Lx = Cx + r * Math.cos(base + a), Ly = Cy + r * Math.sin(base + a);
  return `M ${Tx} ${Ty} L ${Rx} ${Ry} A ${r} ${r} 0 1 0 ${Lx} ${Ly} Z`;
}

/** Mark elements (two boxes + gutter + wedge + hole) for an SxS box, clipped so
 *  the boxes/head/hole overrun never spills when embedded in the lockup canvas.
 *  When `gradient` is set the panels (and hole) are painted in the Aurora
 *  gradient; otherwise they use the solid `ink`. */
function MarkShapes({
  S,
  ink,
  paper,
  gradient = false,
}: {
  S: number;
  ink: string;
  paper: string;
  gradient?: boolean;
}) {
  const rawId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const clipId = `kmask-${rawId}`;
  const gradId = `kgrad-${rawId}`;
  const r = S * CORNER;
  const g = GUTTER.w * S;
  const xL = GUTTER.cx * S - g / 2;
  const xR = GUTTER.cx * S + g / 2;
  const rr = RBOX_R * S;
  const over = 0.3 * S;
  const boxFill = gradient ? `url(#${gradId})` : ink;
  return (
    <>
      <Defs>
        <ClipPath id={clipId}>
          <Rect width={S} height={S} rx={r} ry={r} />
        </ClipPath>
        {gradient ? (
          <LinearGradient id={gradId} x1={0} y1={0} x2={S} y2={S} gradientUnits="userSpaceOnUse">
            <Stop offset={0} stopColor={AURORA[0]} />
            <Stop offset={1} stopColor={AURORA[1]} />
          </LinearGradient>
        ) : null}
      </Defs>
      <G clipPath={`url(#${clipId})`}>
        <Rect width={S} height={S} fill={paper} />
        <Rect x={-over} y={0} width={xL + over} height={S} fill={boxFill} />
        <Rect x={xR} y={0} width={S + over - xR} height={S} rx={rr} ry={rr} fill={boxFill} />
        <Path d={wedgePath(S)} fill={paper} />
        <Circle cx={HOLE.cx * S} cy={WEDGE.midY * S} r={HOLE.r * S} fill={boxFill} />
      </G>
    </>
  );
}

/** Wordmark stroked-centerline elements + total advance width. */
function buildWordmark(color: string): { nodes: React.ReactNode[]; width: number } {
  const { B, xhTop, kTop, pBot, W } = WM;
  const stroke = {
    stroke: color,
    strokeWidth: W,
    fill: 'none',
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  const nodes: React.ReactNode[] = [];
  let x = 0;
  let key = 0;
  const P = (d: string) => nodes.push(<Path key={key++} d={d} {...stroke} />);

  // k — stem + upper arm + lower leg meeting at a single junction
  {
    const sx = x + W / 2;
    const jY = 218;
    P(`M ${sx} ${kTop} L ${sx} ${B}`);
    P(`M ${sx} ${jY} L ${sx + 118} ${xhTop}`);
    P(`M ${sx} ${jY} L ${sx + 132} ${B}`);
    x = sx + 132 + W / 2 + 34;
  }
  // i — stem + dot (×2)
  const iGlyph = () => {
    const sx = x + W / 2;
    P(`M ${sx} ${xhTop} L ${sx} ${B}`);
    nodes.push(<Circle key={key++} cx={sx} cy={xhTop - 52} r={W * 0.62} fill={color} />);
    x = sx + W / 2 + 40;
  };
  iGlyph();
  iGlyph();
  // p — stem (to descender) + round bowl
  {
    const sx = x + W / 2;
    const R = (B - xhTop) / 2;
    const cx = sx + R, cy = xhTop + R;
    P(`M ${sx} ${xhTop} L ${sx} ${pBot}`);
    nodes.push(<Circle key={key++} cx={cx} cy={cy} r={R - W / 2} {...stroke} />);
    x = cx + (R - W / 2) + W / 2 + 38;
  }
  // s — geometric double-arc centerline
  {
    const sW = 132, top = xhTop, bot = B, mid = (top + bot) / 2;
    const sx = x, rgt = sx + sW, lft = sx;
    P(
      [
        `M ${rgt - 6} ${top + 40}`,
        `C ${rgt - 14} ${top - 8} ${lft + 10} ${top - 6} ${lft + 6} ${top + 58}`,
        `C ${lft + 2} ${mid - 6} ${rgt - 8} ${mid + 2} ${rgt - 6} ${bot - 58}`,
        `C ${rgt - 10} ${bot + 6} ${lft + 14} ${bot + 8} ${lft + 6} ${bot - 40}`,
      ].join(' '),
    );
    x = rgt + W / 2;
  }

  return { nodes, width: x };
}

export interface KiipsLogoProps {
  /** Rendered height in px (width derives from the lockup aspect ratio). */
  height?: number | undefined;
  /** Show only the pin mark (square), not the wordmark. */
  markOnly?: boolean | undefined;
  /** Ink override; defaults to the theme's on-surface colour. Passing a colour
   *  also implies `mono` (a solid mark in that colour, no Aurora gradient). */
  color?: string | undefined;
  /** Pin fill (the "knockout"); defaults to the surface the logo sits on. */
  pinColor?: string | undefined;
  /** Render the monochrome mark (solid ink panels) instead of the default
   *  Aurora gradient. Implied when `color` is set. */
  mono?: boolean | undefined;
  accessibilityLabel?: string | undefined;
}

/** Themed kiips logo. The mark is painted in the Aurora gradient by default and
 *  the wordmark reads the current theme so it inverts in dark mode. */
export function KiipsLogo({
  height = 20,
  markOnly = false,
  color,
  pinColor,
  mono = false,
  accessibilityLabel,
}: KiipsLogoProps) {
  const theme = useThemeColors();
  const ink = color ?? theme.textOnSurface;
  const paper = pinColor ?? theme.surface;
  // Gradient mark unless explicitly monochrome or given a solid ink override.
  const gradient = !mono && color === undefined;

  if (markOnly) {
    const S = 100;
    return (
      <Svg
        width={height}
        height={height}
        viewBox={`0 0 ${S} ${S}`}
        accessibilityLabel={accessibilityLabel ?? 'kiips'}
      >
        <MarkShapes S={S} ink={ink} paper={paper} gradient={gradient} />
      </Svg>
    );
  }

  // Lockup layout — mirrors brand.mjs logoSvg().
  const wm = buildWordmark(ink);
  const markS = (WM.pBot - WM.kTop) * 1.18;
  const gap = markS * 0.34;
  const markY = (WM.kTop + WM.pBot) / 2 - markS / 2;
  const wmX = markS + gap;
  const pad = markS * 0.18;
  const vbTop = Math.min(markY, WM.kTop) - pad;
  const vbBot = Math.max(markY + markS, WM.pBot) + pad;
  const vbW = wmX + wm.width + pad * 2;
  const vbH = vbBot - vbTop;

  return (
    <Svg
      width={(height * vbW) / vbH}
      height={height}
      viewBox={`${-pad} ${vbTop} ${vbW} ${vbH}`}
      accessibilityLabel={accessibilityLabel ?? 'kiips'}
    >
      <G transform={`translate(0, ${markY})`}>
        <MarkShapes S={markS} ink={ink} paper={paper} gradient={gradient} />
      </G>
      <G transform={`translate(${wmX}, 0)`}>{wm.nodes}</G>
    </Svg>
  );
}
